import { useEffect, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { AlertCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { useProductCatalog, useProductCatalogFacets } from '@/hooks/useProducts'
import { ISBT_LABELS, SUMMARY_COLUMN_KEYS, formatIsbtValue } from '@/lib/isbtCatalog'
import { CatalogFilterBar, clearedCatalogFilters } from '@/components/products/CatalogFilterBar'
import type { ProductCatalogEntry, ProductCatalogFilters } from '@/types/products'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

/**
 * The filterable product-code catalog table — one selectable row. Shared by
 * Create Product and Receive Product so the two flows can never disagree on how
 * a product code is chosen.
 *
 * Paged and filtered SERVER-side. It used to fetch all catalog rows and both
 * filter and derive its dropdown options from them in memory, which worked while
 * the catalog was a handful of curated rows; the ISBT 128 standard is 14,911.
 *
 * The selected entry is held by the caller, so a row picked on page 1 survives
 * paging to page 4 and back.
 *
 * Active codes only (z8nrz7e72t): a code the tenant has inactivated is retired
 * for new products. "Search inactive" lists those so a person can see why a
 * code is missing, but they render greyed with their note and cannot be
 * picked — the server refuses them too.
 */

const CATALOG_TABLE_ID = 'product-catalog'
const PAGE_SIZE = 25

// The radio and Product Code are locked: without the radio nothing can be
// picked, and the code is what identifies the row.
const CATALOG_COLUMNS: TableColumnDef[] = [
  { id: 'select', header: 'Select', locked: true },
  ...SUMMARY_COLUMN_KEYS.map((key, i) => ({
    id: key,
    header: ISBT_LABELS[key],
    locked: i === 0,
  })),
]

export function ProductCatalogPicker({
  selected,
  onSelect,
}: {
  selected: ProductCatalogEntry | null
  onSelect: (entry: ProductCatalogEntry) => void
}) {
  const cols = useTableColumns(CATALOG_TABLE_ID, CATALOG_COLUMNS)
  const [filters, setFilters] = useState<ProductCatalogFilters>(clearedCatalogFilters('picker', PAGE_SIZE))
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    const id = setTimeout(
      () => setFilters(prev => ({ ...prev, search: searchText || undefined, page: 1 })),
      300,
    )
    return () => clearTimeout(id)
  }, [searchText])

  const { data, isLoading, isError, error } = useProductCatalog(filters)
  const { data: facets } = useProductCatalogFacets(filters)

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const page = data?.page ?? 1
  const pageCount = Math.max(1, Math.ceil(total / (data?.pageSize || PAGE_SIZE)))

  return (
    <>
      <CatalogFilterBar
        filters={filters}
        facets={facets}
        onChange={setFilters}
        searchValue={searchText}
        onSearchChange={setSearchText}
        statusMode='picker'
      />

      {filters.status === 'inactive' && (
        <p className='text-xs text-muted-foreground'>
          Showing Inactive codes. They cannot be used for a new product — a code is activated in
          Configure → Products.
        </p>
      )}

      {/* The picked row is echoed above the table: with 14,911 codes it can be
          several pages away by the time the dialog is confirmed. */}
      {selected && (
        <p className='text-xs text-muted-foreground'>
          Selected <span className='font-mono font-medium'>{selected.productDescriptionCode}</span>
          {selected.productClass ? ` · ${selected.productClass}` : ''}
        </p>
      )}

      <div className='max-h-[42vh] overflow-auto rounded-lg border'>
        {isLoading && (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}
        {isError && (
          <div className='flex items-center gap-2 px-4 py-6 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            Could not load products. {(error as Error)?.message}
          </div>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <p className='py-10 text-center text-sm text-muted-foreground'>
            {filters.status === 'inactive'
              ? 'No Inactive codes match those filters.'
              : 'No Active products match those filters. Try Search inactive.'}
          </p>
        )}
        {!isLoading && !isError && items.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={def.id === 'select' ? 'w-8' : undefined}
                    resizable={def.id !== 'select'}
                  >
                    {def.id === 'select' ? null : def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(entry => (
                <TableRow
                  key={entry.id}
                  onClick={entry.active ? () => onSelect(entry) : undefined}
                  className={cn(
                    entry.active ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
                    entry.id === selected?.id && 'bg-primary/5',
                  )}
                  title={entry.active ? undefined : inactiveTitle(entry.note)}
                >
                  <ColumnCells
                    columns={cols}
                    cells={{
                      select: (
                        <TableCell>
                          <input
                            type='radio'
                            checked={entry.id === selected?.id}
                            disabled={!entry.active}
                            onChange={() => onSelect(entry)}
                            aria-label={`Select ${entry.productDescriptionCode} ${entry.productClass}`}
                          />
                        </TableCell>
                      ),
                      ...Object.fromEntries(
                        SUMMARY_COLUMN_KEYS.map(key => [
                          key,
                          <TableCell
                            key={key}
                            className={
                              key === 'productDescriptionCode'
                                ? 'font-mono font-medium text-primary'
                                : key === 'description'
                                  ? 'max-w-[24rem] truncate font-mono text-xs text-muted-foreground'
                                  : undefined
                            }
                            title={key === 'description' ? entry.description : undefined}
                          >
                            {formatIsbtValue(entry, key)}
                          </TableCell>,
                        ]),
                      ),
                    }}
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Shown at zero too — see ProductsSettings. */}
      {!isError && !isLoading && (
        <div className='flex items-center justify-between text-xs text-muted-foreground'>
          <span>{total.toLocaleString()} matching</span>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              className='rounded border p-1 disabled:opacity-40'
              disabled={page <= 1}
              onClick={() => setFilters(prev => ({ ...prev, page: page - 1 }))}
              aria-label='Previous page'
            >
              <ChevronLeft className='h-3.5 w-3.5' />
            </button>
            <span className='tabular-nums'>
              {page.toLocaleString()} / {pageCount.toLocaleString()}
            </span>
            <button
              type='button'
              className='rounded border p-1 disabled:opacity-40'
              disabled={page >= pageCount}
              onClick={() => setFilters(prev => ({ ...prev, page: page + 1 }))}
              aria-label='Next page'
            >
              <ChevronRight className='h-3.5 w-3.5' />
            </button>
          </div>
        </div>
      )}
    </>
  )
}

/** Why a greyed row cannot be picked, with the tenant's note when there is one. */
function inactiveTitle(note: string): string {
  return note ? `Inactive — ${note}` : 'Inactive in this tenant'
}
